import type { ReactNode } from 'react'
import { MoveLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Header from './Header'
import ProfilePillMenu from './ProfilePillMenu'
import { apiLogout } from '../api/auth/authApi'

type ProfileModalShellProps = {
  children: ReactNode
  showBack?: boolean
}

/**
 * Shared shell for account modal pages (Saved Places / Notifications / Profile).
 * Right-side account menu with Figma padding — not used on the homepage.
 */
export default function ProfileModalShell({
  children,
  showBack = true,
}: ProfileModalShellProps) {
  const navigate = useNavigate()

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigate('/')
  }

  const handleLogout = async () => {
    await apiLogout()
    localStorage.removeItem('user_email')
    localStorage.removeItem('auth_token')
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-50 border-b border-[#F0F0F0] bg-white px-[clamp(28px,4vw,62px)]">
        <Header compact hideProfileMenu />
      </div>

      <main className="min-h-[calc(100vh_-_79px)] bg-[rgba(228,228,228,0.46)] px-[clamp(28px,4.5vw,90px)] pb-[120px] pt-[clamp(38px,5.5vh,74px)]">
        <div className="mx-auto flex w-full max-w-[1440px] items-start justify-between gap-[24px]">
          <div className="min-w-0 flex-1">{children}</div>

          {/* Right-side account menu — Figma 2027:38036 */}
          <aside className="sticky top-[100px] z-40 hidden w-[251px] shrink-0 lg:block">
            <ProfilePillMenu variant="panel" onLogout={handleLogout} />
          </aside>
        </div>
      </main>

      {showBack && (
        <button
          type="button"
          onClick={handleBack}
          className="fixed bottom-[28px] left-[clamp(28px,4.5vw,90px)] z-[70] inline-flex h-[58px] w-[174px] items-center justify-center gap-[7px] rounded-[25px] bg-black font-host text-[16px] font-semibold tracking-[-0.352px] text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition hover:bg-[#202020] active:scale-[0.98]"
        >
          <MoveLeft className="h-[22px] w-[22px] text-[#A5C91C]" />
          Back
        </button>
      )}
    </div>
  )
}